/**
 * Locale-aware number / currency / percent formatting helpers.
 *
 * Used by delivery-funnel UI (bd be0.5) and free for reuse elsewhere.
 * Uses `Intl.NumberFormat` under the hood — the browser locale determines
 * thousands separators and currency formatting.
 */

/**
 * Format an integer with locale-aware thousands separators.
 * Example: 48000 → "48,000" (en-US) / "48.000" (de-DE).
 */
export function formatInteger(value: number): string {
    return new Intl.NumberFormat(undefined, {maximumFractionDigits: 0}).format(value);
}

/**
 * Format a currency amount with locale-aware separators + currency symbol.
 * Falls back to USD when no code is provided.
 * Example: formatCurrency(7.81) → "$7.81" (en-US).
 */
export function formatCurrency(value: number, currencyCode: string = 'USD'): string {
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        // Show cents when the value is small enough that integers would lose info.
        maximumFractionDigits: Math.abs(value) < 100 ? 2 : 0,
    }).format(value);
}

/**
 * Format a 0..1 ratio as a locale-aware percentage with no decimal places.
 * Example: formatPercent(0.39) → "39%".
 */
export function formatPercent(ratio: number): string {
    return new Intl.NumberFormat(undefined, {
        style: 'percent',
        maximumFractionDigits: 0,
    }).format(ratio);
}
